import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';

import { selectCurrentEnvironmentName } from '../../../../environment/selectors/current-environment-name.selector.ts';
import { DefaultPathEnum } from '../../../../main/constants/default-path.ts';
import { ThunkApiConfigType } from '../../../../main/store/main.store.ts';
import { LessonTaskStepKey } from '../../../../main/types/asset/lesson.types.ts';
import { getUrlUtil } from '../../../../main/utils/url.util.ts';

import { mapIsLessonAvailable } from '../../../mappers/is-lesson-available.mapper.ts';
import { mapIsLessonImplemented } from '../../../mappers/is-lesson-implemented.mapper.ts';
import { selectFirstIncompleteLessonTaskStep } from '../../../selectors/first-incomplete-lesson-task-step.selector.ts';
import { selectIsGodMode } from '../../../selectors/is-god-mode.selector.ts';

import { resetLessonTempProgress } from '../../progress/progress.slice.ts';

import { setCurrentLessonTaskStepKey } from '../lesson.slice.ts';
import { LESSON_SLICE_NAME, LessonSliceState } from '../lesson.slice.types.ts';

import { preloadLessonAssetsThunk } from './preload-lesson-assets.thunk.ts';
import { restartCurrentStepThunk } from './restart-current-step.thunk.ts';

interface OpenLessonThunkProps {
  lessonKey: number;
}

interface OpenLessonThunkReturn {
  isAvailable: boolean;
  stepKey?: LessonTaskStepKey;
}

export const openLessonThunk = createAsyncThunk<OpenLessonThunkReturn, OpenLessonThunkProps, ThunkApiConfigType>(
  `${LESSON_SLICE_NAME}/openLessonThunk`,
  async (props: OpenLessonThunkProps, thunkApi): Promise<OpenLessonThunkReturn> => {
    const state = thunkApi.getState();
    const isGodMode = selectIsGodMode(state);

    const firstIncompleteLessonTaskStepKey = selectFirstIncompleteLessonTaskStep(state);
    const environmentName = selectCurrentEnvironmentName(state);
    const isImplemented = mapIsLessonImplemented(props.lessonKey, environmentName);
    const isAvailable = mapIsLessonAvailable(
      isGodMode,
      firstIncompleteLessonTaskStepKey.lesson,
      {
        lesson: props.lessonKey,
      },
      isImplemented
    );

    if (!isAvailable) {
      return { isAvailable };
    }

    const handleLessonStart = async (stepKey: LessonTaskStepKey): Promise<OpenLessonThunkReturn> => {
      thunkApi.dispatch(resetLessonTempProgress());
      if (isAvailable) {
        // We need to ensure that the step key is set before we update the app state
        await thunkApi.dispatch(preloadLessonAssetsThunk({ lessonKey: stepKey.lesson })).unwrap();
        thunkApi.dispatch(setCurrentLessonTaskStepKey(stepKey));
        await thunkApi.dispatch(restartCurrentStepThunk()).unwrap();
      }
      return { isAvailable, stepKey };
    };

    if (!isGodMode && firstIncompleteLessonTaskStepKey.lesson === props.lessonKey) {
      // Resume lesson from the first incomplete step
      return handleLessonStart(firstIncompleteLessonTaskStepKey);
    }

    // Start lesson from the beginning if it's not the current lesson
    return handleLessonStart({ lesson: props.lessonKey, task: 0, step: 0 });
  }
);

export const openLessonThunkExtraReducers = (
  builder: ActionReducerMapBuilder<LessonSliceState>
): ActionReducerMapBuilder<LessonSliceState> =>
  builder.addCase(openLessonThunk.fulfilled, (_state, action) => {
    if (action.payload.stepKey) {
      getUrlUtil().navigateToUrl(DefaultPathEnum.LEARN_SCREEN);
    }
  });
